
import React, { createContext, useContext, useState, useEffect } from "react";
import { ServiceType } from "./LeadContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { calculateDistance } from "@/utils/geoUtils";

export interface Partner {
  id: string;
  name: string;
  phone: string;
  email: string;
  location: string;
  miniImageUrl?: string;
  miniDescription?: string;
  services: ServiceType[];
  active: boolean;
  leadCount?: number;
  successCount?: number;
  latitude?: number;
  longitude?: number;
  radius?: number;
  sms_opt_out: boolean; // Ensure this field is defined as required
}

interface PartnerContextType {
  partners: Partner[];
  loading: boolean;
  addPartner: (partner: Omit<Partner, "id">) => Promise<void>;
  updatePartner: (id: string, data: Partial<Partner>) => Promise<void>;
  removePartner: (id: string) => Promise<void>;
  getPartnerById: (id: string) => Partner | undefined;
  findNearestPartners: (postalCode: string, service: ServiceType, latitude?: number, longitude?: number, locationText?: string) => Promise<Partner[]>;
  findFairestPartner: (service: ServiceType, latitude: number, longitude: number) => Promise<Partner | null>;
  refreshPartners: () => Promise<void>;
}

const PartnerContext = createContext<PartnerContextType | undefined>(undefined);

export const PartnerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('partners')
        .select('*')
        .order('name');

      if (error) throw error;

      if (data) {
        const transformedPartners = data.map(partner => ({
          id: partner.id,
          name: partner.name,
          phone: partner.phone,
          email: partner.email || '',
          location: partner.location,
          miniImageUrl: partner.mini_image_url || undefined,
          miniDescription: partner.mini_description || undefined,
          services: partner.services as ServiceType[],
          active: partner.active,
          latitude: partner.latitude != null ? Number(partner.latitude) : undefined,
          longitude: partner.longitude != null ? Number(partner.longitude) : undefined,
          radius: partner.radius != null ? Number(partner.radius) : undefined,
          sms_opt_out: partner.sms_opt_out ?? false // Ensure default value if null
        }));

        setPartners(transformedPartners);

        await fetchPartnerLeadCounts(transformedPartners);
      }
    } catch (error) {
      console.error("Error fetching partners:", error);
      toast({
        title: "Σφάλμα",
        description: "Δεν ήταν δυνατή η φόρτωση των συνεργατών.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPartnerLeadCounts = async (partnersList: Partner[]) => {
    try {
      const { data: leadsData, error: leadsError } = await supabase
        .from('leads')
        .select('partner_id, stage');

      if (leadsError) throw leadsError;

      if (leadsData) {
        const updatedPartners = partnersList.map(partner => {
          const partnerLeads = leadsData.filter(lead => lead.partner_id === partner.id);
          const successfulLeads = partnerLeads.filter(lead => lead.stage === 'έκλεισεΗΔουλειά');
          
          return {
            ...partner,
            leadCount: partnerLeads.length,
            successCount: successfulLeads.length
          };
        });

        setPartners(updatedPartners);
      }
    } catch (error) {
      console.error("Error fetching partner lead counts:", error);
    }
  };

  const refreshPartners = async () => {
    await fetchPartners();
  };

  const addPartner = async (partnerData: Omit<Partner, "id">) => {
    try {
      const supabaseData = {
        name: partnerData.name,
        phone: partnerData.phone,
        email: partnerData.email,
        location: partnerData.location,
        mini_image_url: partnerData.miniImageUrl,
        mini_description: partnerData.miniDescription,
        services: partnerData.services,
        active: partnerData.active,
        latitude: partnerData.latitude,
        longitude: partnerData.longitude,
        radius: partnerData.radius || 10,
        sms_opt_out: partnerData.sms_opt_out ?? false // Ensure the field is included
      };

      const { data, error } = await supabase
        .from('partners')
        .insert(supabaseData)
        .select()
        .single();

      if (error) throw error;

      if (data) {
        const newPartner: Partner = {
          id: data.id,
          name: data.name,
          phone: data.phone,
          email: data.email || '',
          location: data.location,
          miniImageUrl: data.mini_image_url || undefined,
          miniDescription: data.mini_description || undefined,
          services: data.services as ServiceType[],
          active: data.active,
          latitude: data.latitude,
          longitude: data.longitude,
          radius: data.radius,
          leadCount: 0,
          successCount: 0,
          sms_opt_out: data.sms_opt_out
        };
        
        setPartners(prevPartners => [...prevPartners, newPartner]);
      }
    } catch (error) {
      console.error("Error adding partner:", error);
      toast({
        title: "Σφάλμα",
        description: "Δεν ήταν δυνατή η προσθήκη νέου συνεργάτη.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const updatePartner = async (id: string, data: Partial<Partner>) => {
    try {
      const supabaseData: any = {};
      if (data.name !== undefined) supabaseData.name = data.name;
      if (data.phone !== undefined) supabaseData.phone = data.phone;
      if (data.email !== undefined) supabaseData.email = data.email;
      if (data.location !== undefined) supabaseData.location = data.location;
      if (data.miniImageUrl !== undefined) supabaseData.mini_image_url = data.miniImageUrl;
      if (data.miniDescription !== undefined) supabaseData.mini_description = data.miniDescription;
      if (data.services !== undefined) supabaseData.services = data.services;
      if (data.active !== undefined) supabaseData.active = data.active;
      if (data.latitude !== undefined) supabaseData.latitude = data.latitude;
      if (data.longitude !== undefined) supabaseData.longitude = data.longitude;
      if (data.radius !== undefined) supabaseData.radius = data.radius;
      if (data.sms_opt_out !== undefined) supabaseData.sms_opt_out = data.sms_opt_out;

      const { error } = await supabase
        .from('partners')
        .update(supabaseData)
        .eq('id', id);

      if (error) throw error;

      setPartners(prevPartners =>
        prevPartners.map(partner =>
          partner.id === id ? { ...partner, ...data } : partner
        )
      );
    } catch (error) {
      console.error("Error updating partner:", error);
      toast({
        title: "Σφάλμα",
        description: "Δεν ήταν δυνατή η ενημέρωση του συνεργάτη.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const removePartner = async (id: string) => {
    try {
      const { data: leadsData, error: leadsError } = await supabase
        .from('leads')
        .select('id')
        .eq('partner_id', id)
        .limit(1);

      if (leadsError) throw leadsError;

      if (leadsData && leadsData.length > 0) {
        throw new Error("Δεν είναι δυνατή η διαγραφή του συνεργάτη επειδή έχει συνδεδεμένες επαφές (leads). Απενεργοποιήστε τον συνεργάτη αντί να τον διαγράψετε.");
      }

      const { error } = await supabase
        .from('partners')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setPartners(prevPartners => prevPartners.filter(partner => partner.id !== id));
    } catch (error) {
      console.error("Error removing partner:", error);
      
      if (error instanceof Error) {
        toast({
          title: "Σφάλμα",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Σφάλμα",
          description: "Δεν ήταν δυνατή η διαγραφή του συνεργάτη.",
          variant: "destructive",
        });
      }
      
      throw error;
    }
  };

  const getPartnerById = (id: string) => {
    return partners.find(partner => partner.id === id);
  };

  // Function to find partners based on text location (city/region name)
  const findPartnersByLocationText = (
    locationText: string,
    eligiblePartners: Partner[]
  ): Partner[] => {
    if (!locationText || locationText.trim().length < 3) {
      return [];
    }

    const normalizedText = locationText.trim().toLowerCase();
    
    // First try to find exact matches in partner locations
    const exactMatches = eligiblePartners.filter(partner => {
      const partnerLocation = partner.location.toLowerCase();
      return partnerLocation.includes(normalizedText);
    });

    if (exactMatches.length > 0) {
      console.log(`Found ${exactMatches.length} partners matching location text: ${locationText}`);
      return exactMatches;
    }

    // Try fuzzy matching by checking if any part of the partner's location contains
    // at least 3 characters of the location text or vice versa
    const fuzzyMatches = eligiblePartners.filter(partner => {
      const partnerLocation = partner.location.toLowerCase();
      
      // Check if any 3+ character substring is shared
      for (let i = 0; i <= normalizedText.length - 3; i++) {
        const substring = normalizedText.substring(i, i + 3);
        if (partnerLocation.includes(substring)) {
          return true;
        }
      }
      
      // Check the reverse - if partner location contains parts of the input
      for (let i = 0; i <= partnerLocation.length - 3; i++) {
        const substring = partnerLocation.substring(i, i + 3);
        if (normalizedText.includes(substring)) {
          return true;
        }
      }
      
      return false;
    });

    console.log(`Found ${fuzzyMatches.length} partners with fuzzy location match for: ${locationText}`);
    return fuzzyMatches;
  };

  const findNearestPartners = async (
    postalCode: string, 
    service: ServiceType,
    latitude?: number,
    longitude?: number,
    locationText?: string
  ): Promise<Partner[]> => {
    const activePartners = partners.filter(
      partner => partner.active && partner.services.includes(service)
    );

    if (activePartners.length === 0) {
      console.log("No active partners found offering this service");
      return [];
    }

    // Case 1: We have coordinates - use direct distance calculation
    if (latitude && longitude) {
      console.log(`Finding partners for coordinates: Lat ${latitude}, Lng ${longitude}`);
      
      const nearbyPartners = activePartners.filter(partner => {
        if (!partner.latitude || !partner.longitude) return false;
        
        const distance = calculateDistance(latitude, longitude, partner.latitude, partner.longitude);
        const isInRange = distance <= (partner.radius || 10);
        
        if (isInRange) {
          console.log(`Partner ${partner.name} is in range (${distance.toFixed(2)}km, radius: ${partner.radius}km)`);
        }
        
        return isInRange;
      });
      
      // Sort partners by distance
      if (nearbyPartners.length > 0) {
        return nearbyPartners.sort((a, b) => {
          if (!a.latitude || !a.longitude || !b.latitude || !b.longitude) return 0;
          
          const distanceA = calculateDistance(latitude, longitude, a.latitude, a.longitude);
          const distanceB = calculateDistance(latitude, longitude, b.latitude, b.longitude);
          
          return distanceA - distanceB;
        });
      }
    }
    
    // Case 2: We have location text (city, region, etc.)
    if (locationText && locationText.trim().length > 2) {
      console.log(`Finding partners by location text: ${locationText}`);
      
      const matchedPartners = findPartnersByLocationText(locationText, activePartners);
      if (matchedPartners.length > 0) {
        // Sort by lead count for fair distribution
        return [...matchedPartners].sort((a, b) => {
          const countA = a.leadCount || 0;
          const countB = b.leadCount || 0;
          return countA - countB;
        });
      }
    }
    
    // Case 3: We have postal code but no coordinates
    if (postalCode && postalCode.trim().length > 0) {
      console.log(`Finding partners for postal code: ${postalCode}`);
      
      // If partners have location data, try to geocode the postal code
      const partnersWithLocation = activePartners.filter(
        p => p.latitude && p.longitude
      );
      
      if (partnersWithLocation.length > 0 && window.google && window.google.maps) {
        try {
          // Attempt to geocode the postal code
          const geocoder = new window.google.maps.Geocoder();
          
          const geocodeResult = await new Promise<google.maps.GeocoderResult | null>((resolve, reject) => {
            geocoder.geocode(
              { 
                address: postalCode, 
                componentRestrictions: { country: "gr" } 
              },
              (results, status) => {
                if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
                  resolve(results[0]);
                } else {
                  console.warn(`Geocoding postal code failed: ${status}`);
                  resolve(null);
                }
              }
            );
          });
          
          if (geocodeResult) {
            const location = geocodeResult.geometry.location;
            const lat = location.lat();
            const lng = location.lng();
            
            console.log(`Geocoded postal code ${postalCode} to coordinates: Lat ${lat}, Lng ${lng}`);
            
            // Extract locality/city information for text matching if needed
            let locality = "";
            let region = "";
            
            if (geocodeResult.address_components) {
              for (const component of geocodeResult.address_components) {
                if (component.types.includes("locality")) {
                  locality = component.long_name;
                }
                if (component.types.includes("administrative_area_level_1")) {
                  region = component.long_name;
                }
              }
            }
            
            // Now we have coordinates, filter partners by distance
            const nearbyPartners = partnersWithLocation.filter(partner => {
              const distance = calculateDistance(lat, lng, partner.latitude!, partner.longitude!);
              return distance <= (partner.radius || 10);
            });
            
            // Sort partners by distance
            if (nearbyPartners.length > 0) {
              return nearbyPartners.sort((a, b) => {
                const distanceA = calculateDistance(lat, lng, a.latitude!, a.longitude!);
                const distanceB = calculateDistance(lat, lng, b.latitude!, b.longitude!);
                
                return distanceA - distanceB;
              });
            }
            
            // If no partners found by distance, try to match by locality/region name 
            if (locality || region) {
              const locationText = [locality, region].filter(Boolean).join(", ");
              console.log(`No partners in range by distance, trying text matching with: ${locationText}`);
              
              const textMatches = findPartnersByLocationText(locationText, activePartners);
              if (textMatches.length > 0) {
                return textMatches;
              }
            }
          }
        } catch (error) {
          console.error("Error geocoding postal code:", error);
        }
      }
      
      // Try to match postal code prefix (first 2-3 digits) directly with partner postal codes
      // This works if partners have postal code information embedded in their location field
      if (postalCode.length >= 2) {
        const postalPrefix = postalCode.substring(0, 3);
        console.log(`Trying to match postal code prefix: ${postalPrefix}`);
        
        const postalMatches = activePartners.filter(partner => {
          // Extract potential postal code from partner location
          const postalMatch = partner.location.match(/\b(\d{5})\b/);
          if (postalMatch) {
            const partnerPostal = postalMatch[1];
            return partnerPostal.startsWith(postalPrefix);
          }
          return false;
        });
        
        if (postalMatches.length > 0) {
          console.log(`Found ${postalMatches.length} partners with matching postal code prefix`);
          return postalMatches;
        }
      }
      
      // Last resort: return all active partners with fewer leads
      console.log("Falling back to all active partners sorted by lead count");
    }
    
    // Case 4: No location data at all - return all active partners
    console.log("No location data, returning all active partners sorted by lead count");
    return [...activePartners].sort((a, b) => {
      const countA = a.leadCount || 0;
      const countB = b.leadCount || 0;
      return countA - countB;
    });
  };

  const findFairestPartner = async (
    service: ServiceType,
    latitude: number,
    longitude: number
  ): Promise<Partner | null> => {
    if (!latitude || !longitude) return null;

    const eligiblePartners = partners.filter(partner => 
      partner.active && 
      partner.services.includes(service)
    );

    if (eligiblePartners.length === 0) return null;

    if (eligiblePartners.length === 1) return eligiblePartners[0];

    const partnersInRange = eligiblePartners.filter(partner => {
      if (!partner.latitude || !partner.longitude || !partner.radius) return false;
      
      const distance = calculateDistance(latitude, longitude, partner.latitude, partner.longitude);
      return distance <= partner.radius;
    });

    if (partnersInRange.length === 0) return null;
    if (partnersInRange.length === 1) return partnersInRange[0];

    const partnersWithScores = partnersInRange.map(partner => {
      const distance = calculateDistance(
        latitude, 
        longitude, 
        partner.latitude!, 
        partner.longitude!
      );
      
      const distanceScore = 1 - (distance / (partner.radius || 10));
      
      const leadCount = partner.leadCount || 0;
      const maxLeads = Math.max(...partnersInRange.map(p => p.leadCount || 0), 1);
      const leadCountScore = 1 - (leadCount / maxLeads);
      
      const successCount = partner.successCount || 0;
      const successRate = leadCount > 0 ? successCount / leadCount : 0;
      
      const score = (leadCountScore * 0.5) + (distanceScore * 0.3) + (successRate * 0.2);
      
      return {
        ...partner,
        score
      };
    });

    partnersWithScores.sort((a, b) => (b.score || 0) - (a.score || 0));
    return partnersWithScores[0];
  };

  return (
    <PartnerContext.Provider
      value={{
        partners,
        loading,
        addPartner,
        updatePartner,
        removePartner,
        getPartnerById,
        findNearestPartners,
        findFairestPartner,
        refreshPartners,
      }}
    >
      {children}
    </PartnerContext.Provider>
  );
};

export const usePartners = () => {
  const context = useContext(PartnerContext);
  if (context === undefined) {
    throw new Error("usePartners must be used within a PartnerProvider");
  }
  return context;
};
